import { Injectable } from '@angular/core';

/**
 * Performance measurement wrapper for scientific validation of deduplication improvements.
 * Based on the empirical measurement framework described in FIN-0028.
 */
@Injectable({ providedIn: 'root' })
export class PerformanceMeasurement {
  private marks = new Map<string, number[]>();
  private baselineMetrics = new Map<string, number>();

  /**
   * Measure the duration of an operation
   * @param name Name of the operation
   * @param operation The operation to measure
   * @returns The duration in milliseconds
   */
  measureOperation<T>(name: string, operation: () => T): T {
    const start = performance.now();
    const result = operation();
    const duration = performance.now() - start;

    // Store measurement for statistical analysis
    const measurements = this.marks.get(name) || [];
    measurements.push(duration);
    this.marks.set(name, measurements);

    // Log for empirical evidence (only in development)
    if (!window.location.hostname.includes('prod')) {
      console.log(`[PERF] ${name}: ${duration.toFixed(2)}ms`);
    }

    return result;
  }

  /**
   * Measure an async operation
   * @param name Name of the operation
   * @param operation The async operation to measure
   * @returns The result of the operation
   */
  async measureAsyncOperation<T>(name: string, operation: () => Promise<T>): Promise<T> {
    const start = performance.now();

    try {
      const result = await operation();
      const duration = performance.now() - start;

      // Store measurement
      const measurements = this.marks.get(name) || [];
      measurements.push(duration);
      this.marks.set(name, measurements);

      // Log for empirical evidence
      if (!window.location.hostname.includes('prod')) {
        console.log(`[PERF] ${name}: ${duration.toFixed(2)}ms`);
      }

      return result;
    } catch (error) {
      const duration = performance.now() - start;
      console.error(`[PERF] ${name} failed after ${duration.toFixed(2)}ms:`, error);
      throw error;
    }
  }

  /**
   * Set a baseline metric for comparison
   * @param operation Name of the operation
   * @param baseline Baseline duration in milliseconds
   */
  setBaseline(operation: string, baseline: number): void {
    this.baselineMetrics.set(operation, baseline);
  }

  /**
   * Compare current measurements with baseline
   * @param operation Name of the operation
   * @returns Comparison results
   */
  compareWithBaseline(operation: string): {
    improvement: number;
    percentageReduction: number;
    statSignificant: boolean;
    measurements: number;
    average: number;
    baseline: number;
  } | null {
    const measurements = this.marks.get(operation);
    const baseline = this.baselineMetrics.get(operation);

    if (!measurements || !baseline) {
      return null;
    }

    const average = measurements.reduce((a, b) => a + b, 0) / measurements.length;
    const improvement = baseline - average;
    const percentageReduction = (improvement / baseline) * 100;

    // Simple statistical significance test
    // Using 10% threshold as per FIN-0028 specification
    const statSignificant = percentageReduction > 10;

    return {
      improvement,
      percentageReduction,
      statSignificant,
      measurements: measurements.length,
      average,
      baseline
    };
  }

  /**
   * Get statistics for a specific operation
   * @param operation Name of the operation
   * @returns Statistics object
   */
  getStatistics(operation: string): {
    count: number;
    min: number;
    max: number;
    average: number;
    median: number;
    p95: number;
    p99: number;
  } | null {
    const measurements = this.marks.get(operation);

    if (!measurements || measurements.length === 0) {
      return null;
    }

    const sorted = [...measurements].sort((a, b) => a - b);
    const count = sorted.length;
    const min = sorted[0];
    const max = sorted[count - 1];
    const average = sorted.reduce((a, b) => a + b, 0) / count;
    const median = sorted[Math.floor(count / 2)];
    const p95 = sorted[Math.floor(count * 0.95)];
    const p99 = sorted[Math.floor(count * 0.99)];

    return { count, min, max, average, median, p95, p99 };
  }

  /**
   * Get all performance measurements
   * @returns Map of all measurements
   */
  getAllMeasurements(): Map<string, number[]> {
    return new Map(this.marks);
  }

  /**
   * Generate a performance report
   * @returns Performance report as a string
   */
  generateReport(): string {
    const report: string[] = ['=== Performance Measurement Report ==='];

    for (const [operation, measurements] of this.marks.entries()) {
      const stats = this.getStatistics(operation);
      if (stats) {
        report.push(`\n${operation}:`);
        report.push(`  Count: ${stats.count}`);
        report.push(`  Average: ${stats.average.toFixed(2)}ms`);
        report.push(`  Median: ${stats.median.toFixed(2)}ms`);
        report.push(`  Min: ${stats.min.toFixed(2)}ms`);
        report.push(`  Max: ${stats.max.toFixed(2)}ms`);
        report.push(`  P95: ${stats.p95.toFixed(2)}ms`);
        report.push(`  P99: ${stats.p99.toFixed(2)}ms`);

        const comparison = this.compareWithBaseline(operation);
        if (comparison) {
          report.push(`  vs Baseline:`);
          report.push(`    Improvement: ${comparison.improvement.toFixed(2)}ms`);
          report.push(`    Reduction: ${comparison.percentageReduction.toFixed(1)}%`);
          report.push(`    Significant: ${comparison.statSignificant ? 'YES' : 'NO'}`);
        }
      }
    }

    // Add deduplication-specific metrics based on FIN-0028
    report.push('\n=== Expected Improvements (FIN-0028) ===');
    report.push('Service Initialization: 100% reduction expected');
    report.push('Cookie Reads: 75% reduction expected');
    report.push('Event Duplication: 65% reduction expected');
    report.push('Total Spans: 84% reduction expected');

    return report.join('\n');
  }

  /**
   * Clear all measurements
   */
  reset(): void {
    this.marks.clear();
    this.baselineMetrics.clear();
  }

  /**
   * Export measurements as JSON for analysis
   * @returns JSON string of measurements
   */
  exportAsJson(): string {
    const data = {
      measurements: Object.fromEntries(this.marks),
      baselines: Object.fromEntries(this.baselineMetrics),
      timestamp: new Date().toISOString()
    };
    return JSON.stringify(data, null, 2);
  }
}